// ═══════════════════════════════════════════════════════════════
// PierreDashboard — Dashboard Personnel Fondateur (données réelles)
// Session 33 — Rewrite complet (ex-hardcodé)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { BookOpen, FolderOpen, DollarSign, MessageSquare, RefreshCw, Shield, Brain, TrendingUp, Scale, Code, Package } from 'lucide-react';

const ICON_MAP = { Brain, TrendingUp, Scale, Code, MessageSquare, Shield, BookOpen, Package };
const ModuleIcon = ({ name, color, size = 16 }) => {
  const Icon = ICON_MAP[name];
  if (Icon) return <Icon size={size} style={{ color: color || 'var(--text-secondary)' }} />;
  return <span style={{ fontSize: `${size}px` }}>{name || ''}</span>;
};

const formatCost = (usd) => {
  if (!usd || usd === 0) return '$0.00';
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
};

const PierreDashboard = ({ onProjectSelect }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await fetch('/api/dashboard/pierre', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        setData(json.data);
      }
    } catch (e) {
      console.error('Erreur dashboard:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement dashboard...</div>;

  const cardStyle = {
    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
    borderRadius: '12px', padding: '20px',
  };

  const statBox = (icon, label, value, color) => (
    <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '14px' }}>
      <div style={{
        width: '42px', height: '42px', borderRadius: '10px',
        background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { size: 20, style: { color } })}
      </div>
      <div>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text)' }}>{value}</div>
      </div>
    </div>
  );

  const projects = data?.projects || [];
  const costs = data?.costs || { total_cost_usd: 0, event_count: 0, by_service: {} };
  const expertise = data?.expertise || { modules_enabled: 0, modules_total: 0, modules: [] };
  const activity = data?.activity || { conversations_7d: 0, total_conversations: 0 };

  const serviceLabels = {
    llm: 'LLM (OpenRouter)', embedding: 'Embeddings', tts: 'Text-to-Speech',
    whisper: 'Whisper STT', supadata: 'Supadata', n8n: 'N8N Workflows',
  };

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BookOpen size={22} style={{ color: '#f59e0b' }} />
            Dashboard Expertise
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Vue d'ensemble personnelle
          </p>
        </div>
        <button onClick={() => loadAll(true)} style={{
          padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500',
          cursor: 'pointer', border: 'none', display: 'flex', alignItems: 'center', gap: '6px',
          background: 'linear-gradient(135deg, #d97706, #b45309)', color: 'white',
        }}>
          <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
        </button>
      </div>

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {statBox(<FolderOpen />, 'Projets actifs', String(projects.length), '#3b82f6')}
        {statBox(<DollarSign />, 'Cout 30j', formatCost(costs.total_cost_usd), '#10b981')}
        {statBox(<Shield />, 'Modules expertise', `${expertise.modules_enabled}/${expertise.modules_total}`, '#8b5cf6')}
        {statBox(<MessageSquare />, 'Conversations (7j)', String(activity.conversations_7d), '#f59e0b')}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        {/* Mes Projets */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FolderOpen size={16} style={{ color: '#3b82f6' }} />
            Mes Projets
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {projects.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Aucun projet</div>
            )}
            {projects.slice(0, 5).map(p => (
              <div key={p.id} onClick={() => onProjectSelect && onProjectSelect(p)} style={{
                padding: '10px', borderRadius: '8px', background: 'var(--input-bg)',
                cursor: onProjectSelect ? 'pointer' : 'default', transition: 'background 0.15s',
              }}
                onMouseEnter={e => onProjectSelect && (e.currentTarget.style.background = 'rgba(59,130,246,0.1)')}
                onMouseLeave={e => onProjectSelect && (e.currentTarget.style.background = 'var(--input-bg)')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>{p.name}</span>
                </div>
                <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  <span>{p.conversation_count} conv.</span>
                  <span>{p.file_count} fichiers</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Couts par service */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DollarSign size={16} style={{ color: '#10b981' }} />
            Couts par service (30j)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {Object.keys(costs.by_service).length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Aucun cout enregistre</div>
            )}
            {Object.entries(costs.by_service).sort((a, b) => b[1] - a[1]).map(([svc, cost]) => {
              const maxCost = Math.max(...Object.values(costs.by_service), 0.001);
              return (
                <div key={svc}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{ color: 'var(--text)', fontWeight: '500' }}>{serviceLabels[svc] || svc}</span>
                    <span style={{ color: '#10b981', fontWeight: '600' }}>{formatCost(cost)}</span>
                  </div>
                  <div style={{ height: '6px', background: 'var(--input-bg)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${(cost / maxCost) * 100}%`, height: '100%', borderRadius: '3px',
                      background: 'linear-gradient(90deg, #10b981, #059669)', transition: 'width 0.5s ease',
                    }} />
                  </div>
                </div>
              );
            })}
            <div style={{ borderTop: '1px solid var(--card-border)', paddingTop: '8px', marginTop: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Total appels</span>
                <span style={{ color: 'var(--text)', fontWeight: '600' }}>{costs.event_count}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modules Expertise */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Shield size={16} style={{ color: '#8b5cf6' }} />
          Modules Expertise ({expertise.modules_enabled} actifs)
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
          {expertise.modules.map(m => (
            <div key={m.id} style={{
              padding: '12px', borderRadius: '10px', background: 'var(--input-bg)',
              border: `1px solid ${m.is_enabled ? (m.color || '#3b82f6') + '30' : 'var(--card-border)'}`,
              opacity: m.is_enabled ? 1 : 0.5,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ModuleIcon name={m.icon} color={m.color} size={16} />
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>{m.name}</span>
              </div>
              <span style={{
                fontSize: '10px', fontWeight: '600', marginTop: '6px', display: 'inline-block',
                padding: '1px 8px', borderRadius: '8px',
                background: m.is_enabled ? `${m.color || '#3b82f6'}15` : 'var(--card-border)',
                color: m.is_enabled ? (m.color || '#3b82f6') : 'var(--text-secondary)',
              }}>
                {m.is_system ? 'Systeme' : m.is_enabled ? 'Actif' : 'Inactif'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PierreDashboard;
